import { SORT_DIRECTION_DESC } from '@disco/constants/SortTypes'
import { endOfDay, isSameDay, startOfDay } from 'date-fns'

import { getFiltersWithValidValues } from './search'
import { extractGroupIdsFromFilters, transformFiltersToV2 } from './search-page'

import type { SearchFilter, SearchFilterValue } from './search'
import type { FilterState } from 'types/filter-page'
import type {
  SourceScope,
  V2Filter,
  V2FilterGroup,
  V2SearchQuery,
} from 'types/search-page'

export type GlobalSearchType = '' | 'track' | 'playlist' | 'album' | 'artist'

// Legacy metadata filter rows from the global-search reducer
// (`state.search.filters`).
export type GlobalSearchFilter = SearchFilter

export type GlobalSearchV2Input = {
  searchTerm: string
  type: GlobalSearchType | null
  sort: string
  sortDirection: string | null
  groups: number[]
  tags: string[]
  nativeTags: number[]
  filters: GlobalSearchFilter[]
  startDate: Date | null
  endDate: Date | null
  duration: string | null
  simSearchUrl?: string | null
  /** Prompt (LLM) search mode → v2 query type 'llm'. A pasted URL still wins,
   * routing to simsearch. */
  isPromptSearch?: boolean
  showcasesEnabled?: boolean
  size: number
  scope: SourceScope[]
  cursor?: string
  /** Correlates the parallel business and mds requests of one search. */
  searchId: string
  /**
   * Browse (prototype) path: the "+ Filter" badges emit FilterState[] directly.
   * When present, the v2 filters and group ids are built from it via the shared
   * search-page transform, and the legacy `filters`/`groups`/`nativeTags`/
   * `tags`/`startDate`/`endDate`/`duration` inputs are ignored.
   */
  filterState?: FilterState[]
}

// Mirrors the legacy `disco_*` query params the backend exposed as dedicated
// filter fields (see l2clientfacing/search/filters.py `from_legacy_args`).
const DISCO_FILTER_FIELDS: Record<string, string> = {
  comment: 'notes',
  client_comment: 'client_notes',
  internal_comment: 'internal_notes',
}

// "more than 2000" means >= 2001, matching the legacy `adjustFilterValue`
// behaviour; non-numeric values (including arrays) pass through unadjusted.
const adjustFilterValue = (
  value: SearchFilterValue | undefined,
  delta: -1 | 1,
): SearchFilterValue | undefined => {
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && /^[0-9]+$/.test(value))
  ) {
    const numeric = typeof value === 'number' ? value : parseInt(value)
    return (numeric + delta).toString()
  }
  return value
}

const metadataFilterToV2 = (filter: GlobalSearchFilter): V2Filter => {
  const { key, value, type, start, end } = filter

  if (type === 'range') {
    return { field: key, operator: 'between', value: [start, end] }
  }
  if (type === 'less than' || type === 'before') {
    return { field: key, operator: 'lte', value: adjustFilterValue(value, -1) }
  }
  if (type === 'more than' || type === 'after') {
    return { field: key, operator: 'gte', value: adjustFilterValue(value, 1) }
  }
  return {
    field: key,
    operator: Array.isArray(value) ? 'equals' : 'contains',
    value,
  }
}

const durationToV2 = (duration: string): V2Filter | null => {
  const [rawMin, rawMax] = duration.split('-')
  const min = rawMin ? parseInt(rawMin) : null
  const max = rawMax ? parseInt(rawMax) : null

  if (min && max) {
    return { field: 'duration', operator: 'between', value: [min, max] }
  }
  if (min) return { field: 'duration', operator: 'gte', value: min }
  if (max) return { field: 'duration', operator: 'lte', value: max }
  return null
}

const dateRangeToV2 = (
  startDate: Date | null,
  endDate: Date | null,
): V2Filter | null => {
  if (!startDate || !endDate) return null

  const allTime =
    (startDate.getTime() === new Date(0).getTime() ||
      startDate.getTime() < 0) &&
    isSameDay(endDate, new Date())
  if (allTime) return null

  return {
    field: 'date',
    operator: 'between',
    value: [
      startOfDay(startDate).toISOString(),
      endOfDay(endDate).toISOString(),
    ],
  }
}

/**
 * Builds the `POST /api/v2/search/` request body for the global search bar
 * from the legacy global-search redux state, preserving the semantics of the
 * legacy `GET /api/search/` query construction in `SearchActions`.
 */
export const buildGlobalV2SearchQuery = (
  input: GlobalSearchV2Input,
): V2SearchQuery => {
  const {
    searchTerm,
    type,
    sort,
    sortDirection,
    groups,
    tags,
    nativeTags,
    filters,
    startDate,
    endDate,
    duration,
    simSearchUrl,
    isPromptSearch,
    showcasesEnabled,
    size,
    scope,
    cursor,
    searchId,
    filterState,
  } = input

  const v2Filters: V2Filter[] = []

  if (type === 'album') {
    v2Filters.push({
      field: 'playlist_types',
      operator: 'equals',
      value: 'useralbum',
    })
  }

  let metadataFiltersSet: boolean
  // Group ids and track-tag count drive search_in.groups and the track-forcing
  // sort/result_types below; their source differs between the two filter paths.
  let effectiveGroups = groups
  let effectiveNativeTagCount = nativeTags.length

  if (filterState) {
    // Browse (prototype) path: reuse the same FilterState -> v2 transform the
    // search page uses instead of round-tripping through legacy discrete params.
    // Group filters are excluded by transformFiltersToV2 and applied via
    // search_in.groups.
    const transformed = transformFiltersToV2(filterState)
    if (transformed) {
      transformed.filters.forEach(filter => v2Filters.push(filter as V2Filter))
    }
    effectiveGroups = extractGroupIdsFromFilters(filterState) ?? []
    const trackTagFilter = filterState.find(
      filter =>
        filter.definition.field === 'track_tags' &&
        Array.isArray(filter.value) &&
        filter.value.length > 0,
    )
    effectiveNativeTagCount = trackTagFilter
      ? (trackTagFilter.value as unknown[]).length
      : 0
    // Everything past the album marker and the track-tags filter is a metadata
    // filter for the sort tiebreak.
    metadataFiltersSet =
      v2Filters.length > (type === 'album' ? 1 : 0) + (trackTagFilter ? 1 : 0)
  } else {
    const metaFilters = getFiltersWithValidValues(
      filters.filter(filter => !filter.key.startsWith('disco_')),
    )
    metaFilters.forEach(filter => v2Filters.push(metadataFilterToV2(filter)))

    filters
      .filter(filter => filter.key.startsWith('disco_') && filter.value)
      .forEach(({ key, value }) => {
        const legacyParam = key.replace(/^disco_/, '')
        v2Filters.push({
          field: DISCO_FILTER_FIELDS[legacyParam] ?? legacyParam,
          operator: 'contains',
          value,
        })
      })
    metadataFiltersSet = v2Filters.length > (type === 'album' ? 1 : 0)

    if (nativeTags.length) {
      v2Filters.push({
        field: 'track_tags',
        operator: 'equals',
        value: nativeTags,
      })
    }
    if (tags.length) {
      v2Filters.push({ field: 'playlist_tags', operator: 'equals', value: tags })
    }

    const durationFilter = duration ? durationToV2(duration) : null
    if (durationFilter) v2Filters.push(durationFilter)

    const dateFilter = dateRangeToV2(startDate, endDate)
    if (dateFilter) v2Filters.push(dateFilter)
  }

  const isSimSearch = Boolean(simSearchUrl)
  // A pasted URL routes to simsearch even in prompt mode, so URL wins.
  const isPrompt = Boolean(isPromptSearch) && !isSimSearch
  // Legacy forced type=track when native tags were set; the backend also
  // forces tracks for simsearch.
  let resultTypes: string[]
  // Similarity and prompt (LLM) search only ever return tracks.
  if (isSimSearch || isPrompt || effectiveNativeTagCount) {
    resultTypes = ['track']
  } else if (type === 'album' || type === 'playlist') {
    resultTypes = ['playlist']
  } else if (type) {
    resultTypes = [type]
  } else {
    resultTypes = showcasesEnabled
      ? ['artist', 'playlist', 'track']
      : ['playlist', 'track']
  }

  const sortParam =
    !sortDirection || sortDirection === SORT_DIRECTION_DESC ? `-${sort}` : sort
  let sortOrder: string[]
  if (isSimSearch || isPrompt) {
    sortOrder = ['-score']
  } else if (type || effectiveNativeTagCount) {
    sortOrder = [sortParam]
  } else if (metadataFiltersSet) {
    // Track metadata filters without a type: ask for tracks first.
    sortOrder = ['is_playlist', sortParam]
  } else {
    sortOrder = showcasesEnabled
      ? ['-is_playlist', '-is_track', sortParam]
      : ['-is_playlist', sortParam]
  }

  return {
    query: isSimSearch ? (simSearchUrl as string) : searchTerm,
    size,
    type: isSimSearch ? 'simsearch' : isPrompt ? 'llm' : 'regular',
    scope,
    sort: sortOrder,
    result_types: resultTypes,
    event_data: {
      context: 'Browse',
      albums_only: type === 'album',
      search_id: searchId,
    },
    ...(cursor && { cursor }),
    ...(effectiveGroups.length > 0 && {
      search_in: {
        groups: effectiveGroups,
        groups_descendants: true,
        include_playlist_tracks: true,
      },
    }),
    ...(v2Filters.length > 0 && {
      filters: { operator: 'and', filters: v2Filters } as V2FilterGroup,
    }),
  }
}
